import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Drawer, Input, Spin } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import type { ColDef } from "ag-grid-enterprise";
import type { AgGridReact } from "ag-grid-react";
import CiqAgGrid from "components/ciq-ag-grid";
import { ProjectContext } from "context/ProjectProvider";
import type { DesignScopeListItem } from "mock-data/design-scope";
import { useDesignScopeList } from "../hooks/use-design-scope-list";
import { ClassBadge } from "../components/scope-ui";
import { filterDesignScopeList } from "./design-scope-list-filter";
import {
  buildMasterformatFilter,
  buildUniformatFilter,
  ClassificationFilterMenu,
  masterformatDivisionId
} from "./classification-filter-menu";

type ScopeRow = {
  id: string;
  masterformat: string;
  uniformat: string;
  /** MasterFormat division, which is what its filter matches on. */
  masterformatDivision: string;
  name: string;
};

function classOf(
  item: DesignScopeListItem,
  system: "master-format" | "uni-format"
) {
  const hit = item.classifications.find((c) => c.system === system);
  return { code: hit?.number ?? "", title: hit?.title ?? "" };
}

function nameOf(item: DesignScopeListItem): string {
  if (item.title) return item.title;
  if (item.description) return item.description;
  return item.classifications[0]?.title ?? "";
}

function toRow(item: DesignScopeListItem): ScopeRow {
  return {
    id: item.id,
    masterformat: classOf(item, "master-format").code,
    uniformat: classOf(item, "uni-format").code,
    masterformatDivision: masterformatDivisionId(item),
    name: nameOf(item)
  };
}

function codeCell(system: "MF" | "UF", code?: string) {
  if (!code) return "—";
  return <ClassBadge system={system} code={code} />;
}

/**
 * The Bid Package Log's scope side panel: every design scope item linked to one
 * bid package, as classification codes plus a name.
 */
export function BidScopeListDrawer({
  open,
  bidPackageId,
  label,
  onClose
}: {
  open: boolean;
  bidPackageId: string;
  label: string;
  onClose: () => void;
}) {
  const { projectToken, tokenRetrievalState } = useContext(ProjectContext);
  const token = projectToken || tokenRetrievalState?.token;
  const { items, loading, reload } = useDesignScopeList(token);
  const gridRef = useRef<AgGridReact<ScopeRow>>(null);

  const [query, setQuery] = useState("");
  const [masterformat, setMasterformat] = useState<string[]>([]);
  const [uniformat, setUniformat] = useState<string[]>([]);

  useEffect(() => {
    if (open) reload();
  }, [open, reload]);

  useEffect(() => {
    setQuery("");
    setMasterformat([]);
    setUniformat([]);
  }, [open, bidPackageId]);

  // Both the grid and the filter menus read only this bid package's items.
  const listItems = useMemo(
    () => filterDesignScopeList(items, { bidPackageId }),
    [items, bidPackageId]
  );
  const rows = useMemo(() => listItems.map(toRow), [listItems]);

  // No heading over the divisions: this view has no milestone to scope them to.
  const masterformatFilter = useMemo(
    () => buildMasterformatFilter(listItems, ""),
    [listItems]
  );
  const uniformatFilter = useMemo(
    () => buildUniformatFilter(listItems),
    [listItems]
  );

  const visibleRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((row) => {
      if (uniformat.length && !uniformat.includes(row.uniformat)) return false;
      if (
        masterformat.length &&
        !masterformat.includes(row.masterformatDivision)
      ) {
        return false;
      }
      if (!q) return true;
      return [row.masterformat, row.uniformat, row.name]
        .join(" ")
        .toLowerCase()
        .includes(q);
    });
  }, [rows, query, masterformat, uniformat]);

  const columnDefs = useMemo<ColDef<ScopeRow>[]>(
    () => [
      {
        colId: "masterformat",
        field: "masterformat",
        headerName: "MasterFormat",
        minWidth: 150,
        flex: 0.7,
        cellRenderer: (p: { data?: ScopeRow }) =>
          codeCell("MF", p.data?.masterformat)
      },
      {
        colId: "uniformat",
        field: "uniformat",
        headerName: "UniFormat",
        minWidth: 150,
        flex: 0.7,
        cellRenderer: (p: { data?: ScopeRow }) =>
          codeCell("UF", p.data?.uniformat)
      },
      {
        colId: "name",
        field: "name",
        headerName: "Name",
        minWidth: 220,
        flex: 1.6,
        cellRenderer: (p: { data?: ScopeRow }) => {
          const name = p.data?.name;
          if (!name) return "—";
          return (
            <span className="block truncate" title={name}>
              {name}
            </span>
          );
        }
      }
    ],
    []
  );

  const title = (
    <div className="flex items-center gap-3">
      <span>{label}</span>
      <span
        className="rounded-full border border-solid border-black/10 px-3 py-0.5 text-sm font-normal text-black/65"
        data-testid="bid-scope-count"
      >
        {visibleRows.length} Scope Item{visibleRows.length === 1 ? "" : "s"}
      </span>
    </div>
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="right"
      size={1080}
      destroyOnHidden
      closable={{ placement: "end" }}
      className="scope-list-drawer"
      data-testid="bid-scope-drawer"
      title={title}
      styles={{
        body: {
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          height: "100%"
        }
      }}
    >
      <div className="flex min-h-0 w-full flex-1 flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Input
            allowClear
            prefix={<SearchOutlined className="text-black/25" />}
            placeholder="Search scope items"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="min-w-[200px] max-w-xs flex-1"
            data-testid="bid-scope-search"
          />
          <ClassificationFilterMenu
            testId="bid-scope-masterformat"
            placeholder="MasterFormat: All"
            allLabel="All MasterFormat codes"
            searchPlaceholder="Search MasterFormat code or name"
            model={masterformatFilter}
            value={masterformat}
            onChange={setMasterformat}
            showOptionCounts
            chipClass="bg-ciq-golden-purple-100 text-ciq-golden-purple-900"
          />
          <ClassificationFilterMenu
            testId="bid-scope-uniformat"
            placeholder="UniFormat: All"
            allLabel="All UniFormat codes"
            searchPlaceholder="Search UniFormat code or name"
            model={uniformatFilter}
            value={uniformat}
            onChange={setUniformat}
            chipClass="bg-ciq-geekblue-100 text-ciq-geekblue-600"
          />
        </div>

        {loading && rows.length === 0 ? (
          <div className="flex min-h-0 flex-1 items-center justify-center">
            <Spin />
          </div>
        ) : (
          <div className="scope-list-grid relative min-h-0 w-full flex-1 [&_.ag-root-wrapper]:h-full [&_.ag-root-wrapper]:w-full [&_.ag-theme-alpine]:h-full [&_.ag-theme-alpine]:w-full">
            <CiqAgGrid<ScopeRow>
              gridRef={gridRef}
              rowData={visibleRows}
              columnDefs={columnDefs}
              getRowId={(params) => params.data.id}
              sideBar={false}
              disableResizingAutoWidth
              rowSelection={{
                mode: "singleRow",
                checkboxes: false,
                enableClickSelection: false
              }}
              defaultColDef={{
                sortable: true,
                filter: false,
                resizable: true,
                flex: 1,
                minWidth: 110
              }}
            />
          </div>
        )}
      </div>
    </Drawer>
  );
}
